import { REWARD_V2_ABI } from '@lfg/abis';
import { useBoolean } from '@lfg/core/hooks';
import { useCallback } from 'react';
import { toast } from 'react-toastify';
import { waitForTransactionReceipt, writeContract } from 'wagmi/actions';

import TransactionSubmittedToast from '@/components/TransactionSubmittedToast';
import { useGA, useWeb3 } from '@/hooks';
import { useWeb3Balance } from '@/providers';
import { useClaimReferralV2Mutation, useFetchReferralStatsV2Query } from '@/store/services/referral.service';
import { useAppSelector } from '@/store/utils';
import { convertToHex, logError } from '@/utils/helpers';

import { ClaimReferralProps, ReferralV1Hook } from '../types/referral.type';

const useReferralV2 = (): ReferralV1Hook => {
  const { config, address } = useWeb3();
  const { trackButtonClickEvent } = useGA();
  const { getWalletBalance } = useWeb3Balance();
  const { nodeId: nft } = useAppSelector(state => state.app);
  const { projectInfo } = useAppSelector(state => state.project);
  const [claimReferralV2] = useClaimReferralV2Mutation();
  const {
    isLoading: isLoadingReferralStats,
    data: referralStats,
    refetch: refetchReferralStats,
  } = useFetchReferralStatsV2Query({ address: address as string, nft, page: 'referral' }, { skip: !address || !nft });

  const { value: isClaiming, setTrue: setIsClaimingTrue, setFalse: setIsClaimingFalse } = useBoolean(false);

  const handleClaimReferral = useCallback(
    async ({ nodeId, onSuccess }: ClaimReferralProps) => {
      if (!address || !projectInfo?.programId) return;
      if (!projectInfo?.referral.referralAddress) {
        toast.error('Referral contract not found');
        return;
      }
      setIsClaimingTrue();
      trackButtonClickEvent({ name: 'claim_rewards', address });
      try {
        const nftAddress = nodeId ?? nft;
        const claimRes = await claimReferralV2({ nft: nftAddress, address });
        if (
          !claimRes.data ||
          claimRes.data.tokens.length === 0 ||
          claimRes.data.amounts.length === 0 ||
          Object.keys(claimRes.data.signature).length === 0
        ) {
          toast.error('Claim referral failed. Please try again or contact to support');
          return;
        }
        const {
          tokens,
          amounts,
          deadline,
          nonce,
          signature: { v, s, r },
        } = claimRes.data;
        const hash = await writeContract(config, {
          abi: REWARD_V2_ABI,
          address: projectInfo.referral.referralAddress as `0x${string}`,
          functionName: 'claimAll',
          args: [convertToHex(`0000000${projectInfo.programId}`), tokens, address, amounts, deadline, nonce, v, r, s],
        });
        toast(TransactionSubmittedToast());
        await waitForTransactionReceipt(config, { hash });
        toast.success('Claimed rewards successfully');
        getWalletBalance();
        refetchReferralStats();
        onSuccess?.();
      } catch (error: unknown) {
        const errorDetails = logError(error, 'handleReferral');
        const errorMessage = errorDetails.message || (error instanceof Error ? error.message : 'Claim referral failed');
        toast.error(errorMessage);
        throw error;
      } finally {
        setIsClaimingFalse();
      }
    },
    [
      address,
      config,
      nft,
      projectInfo,
      claimReferralV2,
      getWalletBalance,
      setIsClaimingFalse,
      setIsClaimingTrue,
      refetchReferralStats,
    ],
  );

  return {
    referralStats,
    isClaiming,
    isLoadingReferralStats,
    handleClaimReferral,
    refetchReferralStats,
  };
};

export { useReferralV2 };
